import React from 'react';
import styled from 'styled-components';
import { Icon } from '../../../../components/common/icon/Icon';
import { useGetCropLiveTradeData } from '../../../../hooks/api/crop-trends/useGetCropLiveTradeData.query';
import { useInfiniteScrollLoadingTarget } from '../../../../hooks/scroll/useInfiniteScrollLoadingTarget';
import { format } from 'date-fns';
import { useGetDownloadAppModalHeight } from '../../../../hooks/modal/useGetDownloadAppModalHeight';

interface ICropLiveTradeDataProps {
  marketCode: string;
  largeCategoryCode: string;
  middleCategoryCode: string;
  smallCategoryCode: string;
}

export const CropLiveTradeData = ({
  marketCode,
  largeCategoryCode,
  middleCategoryCode,
  smallCategoryCode,
}: ICropLiveTradeDataProps) => {
  const {
    data: cropLiveTradeData,
    isLoading: isLoadingCropLiveTradeData,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useGetCropLiveTradeData({
    searchParam: {
      wholesaleMarketCode: marketCode === 'all' ? '' : marketCode!,
      largeCategoryCode,
      middleCategoryCode,
      smallCategoryCode,
      page: 1,
      count: 10,
    },
  });

  const { loadingTargetRef, isLoadingTargetRendered } = useInfiniteScrollLoadingTarget({
    isFetchingNextPage,
    hasNextPage: hasNextPage ?? false,
    triggeredFnWhenInView: fetchNextPage,
  });

  const MAIN_HEAD_HEIGHT = 48;
  const SELECT_WHOLESALE_MARKET_BUTTON_HEIGHT = 60;
  const downloadAppModalHeight = useGetDownloadAppModalHeight();

  return (
    <Container>
      <TitleWrapper>
        <Title>{'실시간 경락가 보기'}</Title>
      </TitleWrapper>
      <TableWrapper>
        <TableHeaderContainer top={downloadAppModalHeight + MAIN_HEAD_HEIGHT + SELECT_WHOLESALE_MARKET_BUTTON_HEIGHT}>
          <ColumnMarket>
            <HeaderTitle>{'도매시장'}</HeaderTitle>
            <HeaderDescription>{'법인명'}</HeaderDescription>
          </ColumnMarket>
          <ColumnSize>
            <HeaderTitle>{'규격'}</HeaderTitle>
            <HeaderDescription>{'원산지명'}</HeaderDescription>
          </ColumnSize>
          <ColumnQuantity>
            <HeaderTitle>{'물량'}</HeaderTitle>
          </ColumnQuantity>
          <ColumnSuccessfulBidPrice>
            <HeaderTitle>{'낙찰가'}</HeaderTitle>
            <HeaderDescription>{'낙찰일시'}</HeaderDescription>
          </ColumnSuccessfulBidPrice>
        </TableHeaderContainer>
        <TableBodyContainer>
          {(!cropLiveTradeData || cropLiveTradeData.pages[0].length === 0) && <EmptyData />}
          {cropLiveTradeData &&
            cropLiveTradeData.pages.map((page) =>
              page.map((tradeData) => (
                <Row key={tradeData.successfulBidDateTime}>
                  <ColumnMarket>
                    <BodyTitle>{tradeData.wholesaleMarketName}</BodyTitle>
                    <BodyDescription>{tradeData.corporationName}</BodyDescription>
                  </ColumnMarket>
                  <ColumnSize>
                    <BodyTitle>{`${tradeData.unitQuantity}${tradeData.unitName} ${tradeData.packageName}`}</BodyTitle>
                    <BodyDescription>{`${tradeData.placeOfOriginName
                      .split(' ')
                      .slice(0, 2)
                      .join(' ')}`}</BodyDescription>
                  </ColumnSize>
                  <ColumnQuantity>
                    <BodyTitle>{tradeData.quantity.toLocaleString()}</BodyTitle>
                  </ColumnQuantity>
                  <ColumnSuccessfulBidPrice>
                    <BodyTitle>{`${tradeData.successfulBidPrice.toLocaleString()}원`}</BodyTitle>
                    <BodyDescription>{format(tradeData.successfulBidDateTime, 'HH:mm:ss')}</BodyDescription>
                  </ColumnSuccessfulBidPrice>
                </Row>
              )),
            )}
          {isLoadingTargetRendered && <LoadingTarget ref={loadingTargetRef} />}
        </TableBodyContainer>
      </TableWrapper>
    </Container>
  );
};

const EmptyData = () => {
  return (
    <EmptyDataContainer>
      <Icon name={'BookBox'} width={60} height={60} />
      <EmptyText>{'오늘 경락가 정보가 없어요'}</EmptyText>
    </EmptyDataContainer>
  );
};

const EmptyDataContainer = styled.div`
  display: flex;
  padding: 40px 0px 24px 0px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  align-self: stretch;
`;

const EmptyText = styled.span`
  color: var(--Light-Scheme-Monochrome-grey70, #a7a9b6);
  text-align: center;

  /* Body/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 21px */
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;

  padding: 0px 16px 32px 16px;
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  align-self: stretch;
`;

const Title = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/X-Large/xl-bold */
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 23.4px */
`;

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`;

const TableHeaderContainer = styled.div<{ top: number }>`
  display: flex;
  align-items: flex-start;
  align-self: stretch;

  position: sticky;
  top: ${(props) => `${props.top}px`};

  padding: 8px 16px;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const Column = styled.div``;

const ColumnMarket = styled(Column)`
  min-width: 80px;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 0 0;
`;

const ColumnSize = styled(Column)`
  min-width: 60px;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 0 0;
`;

const ColumnQuantity = styled(Column)`
  min-width: 30px;
  max-width: 70px;
  padding-left: 8px;

  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1 0 0;
`;

const ColumnSuccessfulBidPrice = styled(Column)`
  width: 60px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-end;
`;

const HeaderTitle = styled.span`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);

  /* Display/Medium/m-bold */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 18.2px */

  @media (max-width: 340px) {
    font-size: 12px;
  }
`;

const HeaderDescription = styled.span`
  color: var(--Light-Scheme-Monochrome-grey70, #a7a9b6);

  /* Display/Small/s-medium */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 15.6px */
`;

const TableBodyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;

  padding: 8px 16px 0px 16px;
`;

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  align-self: stretch;

  padding: 8px 0px;
`;

const BodyTitle = styled.span`
  color: var(--Light-Scheme-Monochrome-offblack, #505363);

  /* Display/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 18.2px */

  @media (max-width: 340px) {
    font-size: 12px;
  }
`;

const BodyDescription = styled.span`
  color: var(--Light-Scheme-Monochrome-grey70, #a7a9b6);

  /* Display/Small/s-medium */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 15.6px */
`;

const LoadingTarget = styled.div``;
